import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import Modal from '@/components/common/modal';
import EventCatalogProductDetails from '@/components/events/event-catalog/event-catalog-product-details.tsx';
import { useGetEventOrdersQuery } from '@/state/queries/orders/use-get-event-orders.ts';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { ORDER_STATUS, type OrderDto } from '@/types/dtos/orders/response/order.dto.ts';
import { PRODUCT_KEYS, type ProductKey } from '@/types/dtos/orders/response/product.dto.ts';
import {
  catalogUsesDisplayFeatureFlags,
  countEventCatalogProductsInStore,
  productUsesContributorSnapshotUnitLabel,
} from '@/constants/product-catalog.ts';
import useUpdateOrderMutation from '@/state/mutations/orders/use-update-order.ts';
import { useNotifications } from '@/hooks/use-notifications';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import BillingForm from '@/components/organizations/edit-active-organization/tabs/billing/billing.form.tsx';
import { editActiveOrganizationSchemaFactory } from '@/schemas/organizations/edit-active-organization.ts';
import type { Address } from '@/types/dtos/organizations/address.dto.ts';
import { ADDRESS_TYPE } from '@/enums/address.enum.ts';
import { useRequestOrderPaymentMutation } from '@/state/mutations/transactions/use-request-order-payment.ts';
import type { ServerError } from '@/types/errors/server.error';
import type { TranslationKey } from '@/i18n/translation-key';
import { roundToTwoDecimals } from '@/helpers/decimal.helper.ts';
import type { EventDto } from '@/types/dtos/events/response/event.dto.ts';
import type { EditActiveOrganization } from '@/components/organizations/edit-active-organization/edit.modal';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import type { TaxClassification } from '@/types/dtos/organizations/response/tax-classification.dto';
import { capitalize } from '@/helpers/string.helper';
import { useTaxClassificationMutation } from '@/state/mutations/vat-validation/use-tax-classification';
import { COUNTRY_TO_CODE_MAP } from '@/constants';
import { useProductsStore } from '@/state/stores/use-products-store';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { useEditOrganizationMutation } from '@/state/mutations/organizations/use-edit-organization';
import type { PatchOrganizationDto } from '@/types/dtos/organizations/request/patch-organization.dto';
import type { OrganizationDto } from '@/types/dtos/organizations/organization.dto';
import BillingAddressDisplay from './billing-address-display';
import PricingDisplay from './pricing-display';
import { useEventStore } from '@/state/stores/use-event-store';
import { getOriginEventId } from '@/helpers/event.helper';
import { canCompleteEventOrder, resolveEventPermissionActions } from '@/utils/event-crew-permissions';
import { getOrderLineItemProductId, mapOrderLineItemsToUpdatePayload } from '@/helpers/order-line-item.helper';
import { useCatalogDisplayFeatureAccess, useFeatureAccess } from '@/hooks/use-feature-access';
import EventCatalogProductsListModal from '@/components/events/event-catalog/event-catalog-products-list.modal.tsx';
import {
  clampRemoteSpeakerDayCheckoutQuantity,
  maxRemoteSpeakerDayPurchaseQuantity,
} from '@/utils/remote-speaker-status.utils';
import { EVENT_CATALOG_ORDERS_FEATURE } from '@/constants/feature-flags';
import {
  checkoutClampPersistSignature,
  checkoutQuantitiesEqual,
  resolveCheckoutQuantitySeed,
} from '@/helpers/checkout-quantity-seed.helper';

const createBillingAddress = (
  orderBillingAddress: Address | undefined,
  orgBillingAddress: Address | undefined,
  formData?: EditActiveOrganization['billingAddress'],
): Address =>
  ({
    ...(orderBillingAddress || {}),
    ...(orgBillingAddress || {}),
    ...(formData || {}),
    type: ADDRESS_TYPE.BILLING,
  }) as Address;

const getOrderLineItemProductKey = (product: OrderDto['lineItems'][number]['product']) =>
  typeof product === 'string' ? undefined : product.key;

type CheckoutOrder = OrderDto & { vatAmount: number };

const toCheckoutOrder = (order: OrderDto): CheckoutOrder => ({
  ...order,
  totalPrice: roundToTwoDecimals(order.totalPrice),
  totalPriceWithVat: roundToTwoDecimals(order.totalPriceWithVat),
  vatAmount: roundToTwoDecimals(order.totalPrice * (order.vat / 100)),
});

const applyQuantitiesToCheckoutOrder = (
  order: CheckoutOrder,
  quantitiesToPersist: Record<string, string>,
): CheckoutOrder => {
  const lineItems = order.lineItems.flatMap((lineItem) => {
    const quantityStr = quantitiesToPersist[lineItem.id];
    if (quantityStr === undefined || parseInt(quantityStr, 10) <= 0) {
      return [];
    }
    return [{ ...lineItem, quantity: parseInt(quantityStr, 10) }];
  });

  const totalPrice = roundToTwoDecimals(
    lineItems.reduce((sum, item) => sum + item.snapshotPricePerUnit * item.quantity, 0),
  );
  const vatAmount = roundToTwoDecimals(totalPrice * (order.vat / 100));
  const totalPriceWithVat = roundToTwoDecimals(totalPrice * (1 + order.vat / 100));

  return {
    ...order,
    lineItems,
    totalPrice,
    totalPriceWithVat,
    vatAmount,
  };
};

const createFormDefaults = (
  activeOrganization: OrganizationDto | undefined,
  billingAddress: Address | undefined,
): EditActiveOrganization => ({
  general: { name: activeOrganization?.name || 'no name' },
  billingAddress: {
    name: billingAddress?.name || '',
    address1: billingAddress?.address1 || '',
    address2: billingAddress?.address2 || '',
    city: billingAddress?.city || '',
    country: billingAddress?.country || '',
    postCode: billingAddress?.postCode || '',
    vat: billingAddress?.vat || '',
  },
});

const EventCatalogOrderCheckoutModal = ({
  open,
  onModalClose,
  event,
  onOpenCatalogPicker,
}: {
  open: boolean;
  onModalClose: () => void;
  event?: EventDto;
  onOpenCatalogPicker?: () => void;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const { hasAccess: hasCatalogOrderAccess, isLoading: catalogOrderAccessLoading } =
    useFeatureAccess(EVENT_CATALOG_ORDERS_FEATURE);
  const isWideScreen = useBreakpointUp('sm');
  const { activeOrganization, setActiveOrganization } = useActiveOrganizationStore();
  const [latestOrder, setLatestOrder] = useState<CheckoutOrder | undefined>();
  const [isEditingBillingAddress, setIsEditingBillingAddress] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [draftQuantities, setDraftQuantities] = useState<Record<string, string>>({});
  const [editingLineItemId, setEditingLineItemId] = useState<string | null>(null);
  const quantityPersistInFlightRef = useRef(false);
  const clampPersistSignatureRef = useRef('');
  const products = useProductsStore((s) => s.products);
  const storeEvent = useEventStore((s) => s.event);
  const storeEventPlan = useEventStore((s) => s.eventPlan);
  const contributors = useEventStore((s) => s.contributors);
  const eventForCheckout = event ?? storeEvent ?? storeEventPlan;
  const originEventId = eventForCheckout ? getOriginEventId(eventForCheckout) : undefined;
  const [taxClassification, setTaxClassification] = useState<TaxClassification>();
  const { data: orders } = useGetEventOrdersQuery(originEventId);
  const { mutateAsync: updateOrderMutation, isPending: updatePending } = useUpdateOrderMutation();
  const { mutateAsync: taxClassificationMutation } = useTaxClassificationMutation();
  const { mutateAsync: requestOrderPaymentMutation, isPending: requestOrderPaymentPending } =
    useRequestOrderPaymentMutation();
  const { mutateAsync: editOrganizationMutation } = useEditOrganizationMutation();
  const { data: permissions } = usePermissionsQuery();
  const eventPermissionActions = useMemo(
    () => resolveEventPermissionActions(permissions, eventForCheckout),
    [permissions, eventForCheckout],
  );
  const canCompleteOrder = canCompleteEventOrder(eventPermissionActions);
  const { featureAccess, isLoading: catalogFlagsLoading } = useCatalogDisplayFeatureAccess();
  const gateCatalogFlags = catalogUsesDisplayFeatureFlags();
  const sellableCatalogCount = useMemo(() => {
    if (gateCatalogFlags && catalogFlagsLoading) {
      return countEventCatalogProductsInStore(products);
    }
    return countEventCatalogProductsInStore(products, { featureAccess });
  }, [gateCatalogFlags, catalogFlagsLoading, products, featureAccess]);
  const [embeddedCatalogOpen, setEmbeddedCatalogOpen] = useState(false);

  const contributorDisplayNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const contributor of contributors ?? []) {
      const name = contributor.profiles[0]?.displayName;
      if (name) {
        map.set(contributor.id, name);
      }
    }
    return map;
  }, [contributors]);

  const eventDayCount = eventForCheckout?.days?.length ?? 0;

  const maxQuantityByLineItemId = useMemo(() => {
    const map = new Map<string, number>();
    for (const lineItem of latestOrder?.lineItems ?? []) {
      const productKey = getOrderLineItemProductKey(lineItem.product);
      if (productKey !== PRODUCT_KEYS.EVENT_REMOTE_SPEAKER_DAY || !lineItem.contributorId) {
        continue;
      }
      const contributor = contributors?.find((entry) => entry.id === lineItem.contributorId);
      map.set(lineItem.id, maxRemoteSpeakerDayPurchaseQuantity(eventDayCount, contributor));
    }
    return map;
  }, [contributors, eventDayCount, latestOrder?.lineItems]);

  const clampLineItemQuantity = useCallback(
    (lineItemId: string, quantity: number): number => {
      const lineItem = latestOrder?.lineItems.find((entry) => entry.id === lineItemId);
      if (!lineItem) {
        return quantity;
      }
      const productKey = getOrderLineItemProductKey(lineItem.product);
      if (productKey !== PRODUCT_KEYS.EVENT_REMOTE_SPEAKER_DAY || !lineItem.contributorId) {
        return quantity;
      }
      const contributor = contributors?.find((entry) => entry.id === lineItem.contributorId);
      return clampRemoteSpeakerDayCheckoutQuantity(quantity, eventDayCount, contributor);
    },
    [contributors, eventDayCount, latestOrder?.lineItems],
  );

  const resolveLineItemScopeLabel = (contributorId?: string) => {
    if (!contributorId) return undefined;
    return t('event.checkoutModal.scopedLineForContributor', {
      name: contributorDisplayNameById.get(contributorId) ?? t('event.detail.contributors.unknownContributor'),
    });
  };

  const hasInvoice = useMemo(() => orders?.some((order) => order.invoiceId !== undefined), [orders]);

  const canEditBillingAddress =
    (!hasInvoice &&
      activeOrganization?.id &&
      permissions?.organizations?.[activeOrganization?.id]?.includes('update')) ||
    false;

  const currentActiveOrganizationBillingAddress = useMemo(
    () => activeOrganization?.addresses.find((address) => address.type === ADDRESS_TYPE.BILLING),
    [activeOrganization?.addresses],
  );

  useEffect(() => {
    if (quantityPersistInFlightRef.current) {
      return;
    }
    if (orders?.length) {
      const latestOrder = orders.find(
        (order) => order.status === ORDER_STATUS.DRAFT || order.status === ORDER_STATUS.PAYMENT_PENDING,
      );
      if (!latestOrder) {
        return;
      }
      setLatestOrder(toCheckoutOrder(latestOrder));
    }
  }, [orders]);

  const isPaymentPending = latestOrder?.status === ORDER_STATUS.PAYMENT_PENDING;

  // Polling moved to AddToCartButton to work even without selectedEvent

  const editActiveOrganizationSchema = editActiveOrganizationSchemaFactory(t);
  const form = useForm<EditActiveOrganization>({
    resolver: zodResolver(editActiveOrganizationSchema),
    values: createFormDefaults(activeOrganization, currentActiveOrganizationBillingAddress),
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      const country = latestOrder?.billingAddress?.country;
      if (!country) return;
      const countryCode = COUNTRY_TO_CODE_MAP[country as keyof typeof COUNTRY_TO_CODE_MAP];
      if (!countryCode) return;
      try {
        const result = await taxClassificationMutation({
          countryCode,
        });
        setTaxClassification(result.taxClassification);
      } catch {
        setTaxClassification(undefined);
      }
    })();
  }, [open, latestOrder?.billingAddress?.country, taxClassificationMutation]);

  const latestOrderBillingCountry =
    latestOrder?.billingAddress?.country && latestOrder?.billingAddress?.country !== ''
      ? latestOrder?.billingAddress?.country
      : undefined;

  const vatCountryDisplay = useMemo(() => {
    const vat = latestOrder?.billingAddress?.vat;
    if (!latestOrderBillingCountry) {
      return t('common.N/A');
    }
    if (taxClassification === 'intraCommunity' && vat && vat !== '') {
      return t('event.checkoutModal.reverseCharge');
    }
    if (taxClassification === 'thirdPartyCountry') {
      return t('event.checkoutModal.thirdPartyCountry');
    }
    return capitalize(latestOrderBillingCountry);
  }, [taxClassification, latestOrder?.billingAddress?.vat, latestOrderBillingCountry]);

  const persistOrderQuantities = useCallback(
    async (
      quantitiesToPersist: Record<string, string>,
      rollback: {
        order: CheckoutOrder;
        quantities: Record<string, string>;
        draftQuantities: Record<string, string>;
      },
    ) => {
      quantityPersistInFlightRef.current = true;
      setLatestOrder(applyQuantitiesToCheckoutOrder(rollback.order, quantitiesToPersist));

      try {
        const updated = await updateOrderMutation({
          orderId: rollback.order.id,
          lineItems: rollback.order.lineItems
            .filter((lineItem) => {
              const quantityStr = quantitiesToPersist[lineItem.id];
              return quantityStr !== undefined && parseInt(quantityStr, 10) > 0;
            })
            .map((lineItem) => {
              const mapped = mapOrderLineItemsToUpdatePayload([lineItem])[0];
              return {
                ...mapped,
                quantity: parseInt(quantitiesToPersist[lineItem.id] || '0', 10),
              };
            }),
        });
        setLatestOrder(toCheckoutOrder(updated));
      } catch {
        setLatestOrder(rollback.order);
        setQuantities(rollback.quantities);
        setDraftQuantities(rollback.draftQuantities);
        notification.notifyError(t('event.checkoutModal.notification.updateOrderError'));
      } finally {
        quantityPersistInFlightRef.current = false;
      }
    },
    [notification, t, updateOrderMutation],
  );

  const handleRemoveLineItemDraft = (lineItemId: string) => {
    if (!latestOrder) {
      return;
    }

    const rollback = {
      order: latestOrder,
      quantities,
      draftQuantities,
    };
    const nextQuantities = { ...quantities, [lineItemId]: '0' };
    setQuantities(nextQuantities);
    setDraftQuantities(nextQuantities);
    setEditingLineItemId(null);
    void persistOrderQuantities(nextQuantities, rollback);
  };

  const updateOrderWithBillingAddress = async () => {
    if (!latestOrder) return;

    try {
      await updateOrderMutation({
        orderId: latestOrder.id,
      });
    } catch {
      notification.notifyError(t('event.checkoutModal.notification.updateOrderError'));
      throw new Error('Failed to update order');
    }
  };

  const billingAddressOnSubmit = async (data: EditActiveOrganization) => {
    if (!latestOrder || !activeOrganization) {
      return;
    }

    const billingAddress = createBillingAddress(
      latestOrder.billingAddress,
      currentActiveOrganizationBillingAddress,
      data.billingAddress,
    );

    const updateData: PatchOrganizationDto = {
      name: data.general.name,
      addresses: [],
    };

    if (
      billingAddress?.name &&
      billingAddress?.address1 &&
      billingAddress?.city &&
      billingAddress?.country &&
      billingAddress?.postCode
    ) {
      updateData.addresses.push({
        name: billingAddress?.name,
        address1: billingAddress?.address1,
        address2: billingAddress?.address2,
        city: billingAddress?.city,
        postCode: billingAddress?.postCode,
        country: billingAddress?.country,
        vat: billingAddress?.vat,
        type: ADDRESS_TYPE.BILLING,
      });
    }

    // first we update org billing address (and fail if we dont have permissions)
    try {
      await editOrganizationMutation({
        id: activeOrganization.id,
        data: updateData,
      });
      setActiveOrganization({
        ...activeOrganization,
        addresses: updateData.addresses,
      });
    } catch {
      notification.notifyError(t('event.checkoutModal.notification.updateOrganizationBillingAddressError'));
      return;
    }

    // secondly we update order billing address to update its tax numbers and change total price
    try {
      await updateOrderMutation({
        orderId: latestOrder.id,
      });
      setIsEditingBillingAddress(false);
    } catch (error) {
      const errorMessage = t('event.checkoutModal.notification.updateOrderError');
      const errorCode = (error as ServerError).errorCode;
      const reason = errorCode ? ` (${t(`error.${errorCode}` as TranslationKey)})` : '';
      notification.notifyError(`${errorMessage}${reason}`);
    }
  };

  const handleRequestOrderPayment = async () => {
    if (!latestOrder || !activeOrganization?.id || !canCompleteOrder) {
      return;
    }

    try {
      // first we update order in case its billing address was changed in org settings
      await updateOrderWithBillingAddress();
    } catch {
      return;
    }

    // then we request payment
    try {
      const res = await requestOrderPaymentMutation({
        orderId: latestOrder.id,
        organizationId: activeOrganization.id,
        redirectUrlAfterPayment: window.location.href,
      });
      if (res?.redirectUrl) {
        window.location.href = res.redirectUrl;
      }
    } catch {
      notification.notifyError(t('event.checkoutModal.notification.requestOrderPaymentError'));
    }
  };

  const billingAddressReset = useCallback(() => {
    form.reset(createFormDefaults(activeOrganization, currentActiveOrganizationBillingAddress));
  }, [form, currentActiveOrganizationBillingAddress, activeOrganization?.name]);

  useEffect(() => {
    billingAddressReset();

    try {
      (async () => {
        if (!latestOrder) return;
        await updateOrderMutation({
          orderId: latestOrder.id,
        });
      })();
      setIsEditingBillingAddress(false);
    } catch {
      notification.notifyError(t('event.checkoutModal.notification.updateOrderError'));
    }
  }, [currentActiveOrganizationBillingAddress]);

  const onEditingBillingAddressCancel = () => {
    setIsEditingBillingAddress(false);
    billingAddressReset();
  };

  const handleClose = () => {
    onModalClose();
    billingAddressReset();
  };

  useEffect(() => {
    if (!latestOrder || editingLineItemId) {
      return;
    }
    const { quantities: initialQuantities, needsClampPersist } = resolveCheckoutQuantitySeed(
      latestOrder.lineItems,
      clampLineItemQuantity,
    );

    // Persisting rebuilds the order object, which re-runs this effect — keep the state objects
    // identical when the seed did not actually change so that cycle cannot become a render loop.
    setQuantities((prev) => (checkoutQuantitiesEqual(prev, initialQuantities) ? prev : initialQuantities));
    setDraftQuantities((prev) => (checkoutQuantitiesEqual(prev, initialQuantities) ? prev : initialQuantities));

    if (!needsClampPersist || quantityPersistInFlightRef.current) {
      return;
    }
    const clampSignature = checkoutClampPersistSignature(latestOrder.id, initialQuantities);
    if (clampPersistSignatureRef.current === clampSignature) {
      return;
    }
    clampPersistSignatureRef.current = clampSignature;

    void persistOrderQuantities(initialQuantities, {
      order: latestOrder,
      quantities: initialQuantities,
      draftQuantities: initialQuantities,
    });
  }, [clampLineItemQuantity, editingLineItemId, latestOrder, persistOrderQuantities]);

  useEffect(() => {
    if (!open) {
      setEmbeddedCatalogOpen(false);
    }
  }, [open]);

  const handleOpenCatalogPickerFromCheckout = useCallback(() => {
    if (onOpenCatalogPicker) {
      onOpenCatalogPicker();
      return;
    }
    setEmbeddedCatalogOpen(true);
  }, [onOpenCatalogPicker]);

  const handleEmbeddedCatalogClose = useCallback(() => {
    setEmbeddedCatalogOpen(false);
  }, []);

  /** User chose an action in the catalog that normally opens checkout — already in checkout, just close the picker. */
  const handleEmbeddedCatalogCheckoutRequested = useCallback(() => {
    setEmbeddedCatalogOpen(false);
  }, []);

  const handleQuantityDraftChange = (lineItemId: string, newValue: string) => {
    setDraftQuantities((prev) => ({ ...prev, [lineItemId]: newValue }));
  };

  const commitQuantityChange = useCallback(
    async (lineItemId: string) => {
      setEditingLineItemId(null);

      const draft = draftQuantities[lineItemId] ?? quantities[lineItemId];
      const committed = quantities[lineItemId];
      if (draft === undefined) {
        return;
      }

      const parsed = parseInt(draft, 10);
      if (!Number.isFinite(parsed) || parsed < 1) {
        if (committed !== undefined) {
          setDraftQuantities((prev) => ({ ...prev, [lineItemId]: committed }));
        }
        return;
      }

      const clamped = clampLineItemQuantity(lineItemId, parsed);
      if (clamped < 1) {
        if (committed !== undefined) {
          setDraftQuantities((prev) => ({ ...prev, [lineItemId]: committed }));
        }
        return;
      }

      const normalized = String(clamped);
      if (normalized === committed) {
        setDraftQuantities((prev) => ({ ...prev, [lineItemId]: normalized }));
        return;
      }

      if (!latestOrder) {
        return;
      }

      const rollback = {
        order: latestOrder,
        quantities,
        draftQuantities,
      };
      const nextQuantities = { ...quantities, [lineItemId]: normalized };
      setQuantities(nextQuantities);
      setDraftQuantities(nextQuantities);
      await persistOrderQuantities(nextQuantities, rollback);
    },
    [clampLineItemQuantity, draftQuantities, latestOrder, persistOrderQuantities, quantities],
  );

  const adjustLineItemQuantity = useCallback(
    async (lineItemId: string, delta: number) => {
      if (!latestOrder || isPaymentPending) {
        return;
      }

      const currentValue = parseInt(draftQuantities[lineItemId] ?? quantities[lineItemId] ?? '1', 10);
      if (!Number.isFinite(currentValue)) {
        return;
      }

      const maxQuantity = maxQuantityByLineItemId.get(lineItemId);
      let nextValue = Math.max(1, currentValue + delta);
      if (maxQuantity !== undefined) {
        nextValue = Math.min(nextValue, maxQuantity);
      }
      if (nextValue === currentValue) {
        return;
      }

      const rollback = {
        order: latestOrder,
        quantities,
        draftQuantities,
      };
      const nextQuantities = { ...quantities, [lineItemId]: String(nextValue) };
      setQuantities(nextQuantities);
      setDraftQuantities(nextQuantities);
      setEditingLineItemId(null);
      await persistOrderQuantities(nextQuantities, rollback);
    },
    [draftQuantities, isPaymentPending, latestOrder, maxQuantityByLineItemId, persistOrderQuantities, quantities],
  );

  if (catalogOrderAccessLoading || !hasCatalogOrderAccess || !latestOrder) {
    return null;
  }

  const visibleLineItems = latestOrder.lineItems.filter((lineItem) => {
    const quantityStr = quantities[lineItem.id];
    if (quantityStr === undefined) {
      return true;
    }
    return parseInt(quantityStr, 10) > 0;
  });

  return (
    <>
      <Modal
        open={open}
        size={isWideScreen ? 'lg' : 'sm'}
        width={isWideScreen ? 1000 : undefined}
        minHeight={isWideScreen ? 360 : undefined}
        maxHeight={isWideScreen ? 'min(92vh, calc(100vh - 48px))' : undefined}
        sx={isWideScreen ? { overflow: 'hidden' } : undefined}
        fullScreen={!isWideScreen}
        data-testid="event-catalog-order-checkout-modal"
        handleClose={handleClose}
        title={t('common.checkout')}
        description={t('event.checkoutModal.description')}
        contentSx={isWideScreen ? { minHeight: 0 } : { overflow: 'auto', minHeight: 0 }}
        footer={
          <>
            <Button onClick={handleClose} variant="outlined" aria-label="Cancel">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleRequestOrderPayment}
              variant="contained"
              aria-label="Proceed"
              loading={requestOrderPaymentPending || isPaymentPending}
              disabled={
                isPaymentPending ||
                visibleLineItems.length === 0 ||
                updatePending ||
                !latestOrder.billingAddress ||
                !canCompleteOrder
              }
            >
              {t('common.proceed')}
            </Button>
          </>
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            ...(isWideScreen ? { flex: 1, minHeight: 0 } : {}),
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              flex: { xs: 'none', sm: 1 },
              minHeight: { xs: 'auto', sm: 0 },
              maxHeight: { xs: 'none', sm: '100%' },
              overflow: { xs: 'visible', sm: 'hidden' },
            }}
          >
            <Box
              sx={{
                flex: { xs: 'none', sm: '1 1 0%' },
                minWidth: 0,
                minHeight: { xs: 'auto', sm: 0 },
                overflowY: { xs: 'visible', sm: 'auto' },
                pr: { sm: 0.5 },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box display="flex" justifyContent="end">
                <Button
                  variant="contained"
                  size="small"
                  sx={{ mb: 2 }}
                  onClick={handleOpenCatalogPickerFromCheckout}
                  disabled={isPaymentPending || latestOrder?.lineItems.length === sellableCatalogCount}
                >
                  + {t('common.feature')}
                </Button>
              </Box>
              <Box>
                {visibleLineItems.map((lineItem) =>
                  (() => {
                    const productId = getOrderLineItemProductId(lineItem.product);
                    const productKey = getOrderLineItemProductKey(lineItem.product);
                    if (!productId) return null;
                    const maxQuantity = maxQuantityByLineItemId.get(lineItem.id);
                    const currentQuantity = parseInt(
                      draftQuantities[lineItem.id] ?? quantities[lineItem.id] ?? lineItem.quantity.toString(),
                      10,
                    );
                    const atMaxQuantity = maxQuantity !== undefined && currentQuantity >= maxQuantity;
                    return (
                      <Box
                        key={lineItem.id}
                        display="flex"
                        flexDirection="column"
                        mb={2}
                        pb={3}
                        borderBottom={1}
                        borderColor="divider"
                      >
                        <EventCatalogProductDetails
                          readonly
                          productKey={(productKey as ProductKey | undefined) ?? PRODUCT_KEYS.EVENT_REMOTE_SPEAKER_DAY}
                          event={eventForCheckout}
                          scopeLabel={resolveLineItemScopeLabel(lineItem.contributorId)}
                          onRemoveLineItem={
                            !isPaymentPending ? () => handleRemoveLineItemDraft(lineItem.id) : undefined
                          }
                        />
                        <Box display="flex" flexDirection="row" alignItems="end">
                          <Stack spacing={0.5} sx={{ flexShrink: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                              {t('common.add')}
                            </Typography>
                            <Stack
                              direction="row"
                              alignItems="stretch"
                              sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                overflow: 'hidden',
                                bgcolor: 'background.paper',
                              }}
                            >
                              <IconButton
                                aria-label={t('common.decrease')}
                                onClick={() => void adjustLineItemQuantity(lineItem.id, -1)}
                                disabled={
                                  isPaymentPending ||
                                  parseInt(
                                    draftQuantities[lineItem.id] ??
                                      quantities[lineItem.id] ??
                                      lineItem.quantity.toString(),
                                    10,
                                  ) <= 1
                                }
                                sx={{
                                  minWidth: { xs: 44, sm: 40 },
                                  minHeight: { xs: 44, sm: 40 },
                                  borderRadius: 0,
                                  borderRight: 1,
                                  borderColor: 'divider',
                                }}
                              >
                                <KeyboardArrowDown sx={{ fontSize: { xs: 28, sm: 24 } }} />
                              </IconButton>
                              <TextField
                                type="number"
                                variant="standard"
                                value={draftQuantities[lineItem.id] ?? quantities[lineItem.id] ?? lineItem.quantity}
                                onFocus={() => setEditingLineItemId(lineItem.id)}
                                onBlur={() => void commitQuantityChange(lineItem.id)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    event.currentTarget.blur();
                                  }
                                }}
                                onChange={(event) => handleQuantityDraftChange(lineItem.id, event.target.value)}
                                disabled={isPaymentPending}
                                inputProps={{
                                  'min': 1,
                                  ...(maxQuantity !== undefined ? { max: maxQuantity } : {}),
                                  'aria-label': t('common.add'),
                                }}
                                sx={{
                                  'width': { xs: 52, sm: 48 },
                                  '& .MuiInputBase-root': {
                                    minHeight: { xs: 44, sm: 40 },
                                  },
                                  '& .MuiInputBase-input': {
                                    textAlign: 'center',
                                    py: { xs: 1.25, sm: 1 },
                                    px: 0.5,
                                    MozAppearance: 'textfield',
                                  },
                                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                                    {
                                      WebkitAppearance: 'none',
                                      margin: 0,
                                    },
                                }}
                              />
                              <IconButton
                                aria-label={t('common.increase')}
                                onClick={() => void adjustLineItemQuantity(lineItem.id, 1)}
                                disabled={isPaymentPending || atMaxQuantity}
                                sx={{
                                  minWidth: { xs: 44, sm: 40 },
                                  minHeight: { xs: 44, sm: 40 },
                                  borderRadius: 0,
                                  borderLeft: 1,
                                  borderColor: 'divider',
                                }}
                              >
                                <KeyboardArrowUp sx={{ fontSize: { xs: 28, sm: 24 } }} />
                              </IconButton>
                            </Stack>
                          </Stack>
                          <Box display="flex" flexDirection="row" alignItems="baseline" ml={2} width="100%">
                            <Typography variant="body1" sx={{ fontSize: '1.5em' }}>
                              €{lineItem.snapshotPricePerUnit}
                            </Typography>
                            <Typography variant="body2">
                              /
                              {lineItem.snapshotUnit === 'count' && productUsesContributorSnapshotUnitLabel(productKey)
                                ? t('common.roleLabels.contributor')
                                : lineItem.snapshotUnit === 'gb'
                                  ? t('common.gigabyte')
                                  : lineItem.snapshotUnit || t('common.N/A')}
                              , {t('event.checkoutModal.excludingVat')}
                            </Typography>
                            <Typography variant="body2" sx={{ ml: 'auto' }}>
                              €
                              {roundToTwoDecimals(
                                lineItem.snapshotPricePerUnit *
                                  parseInt(quantities[lineItem.id] || lineItem.quantity.toString(), 10),
                              ).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })(),
                )}
              </Box>
            </Box>

            <Box
              sx={{
                flex: { xs: 'none', sm: '1 1 0%' },
                maxWidth: { xs: '100%', sm: 350 },
                minWidth: 0,
                boxSizing: 'border-box',
                minHeight: { xs: 'auto', sm: 0 },
                overflowY: { xs: 'visible', sm: 'auto' },
                borderLeft: { xs: 0, sm: 1 },
                borderTop: { xs: 1, sm: 0 },
                borderColor: 'divider',
                pl: { xs: 0, sm: 2 },
                pt: { xs: 2, sm: 0 },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ mb: 3, width: '100%', flexShrink: 0 }}>
                {isEditingBillingAddress ? (
                  <Box
                    component="form"
                    onSubmit={form.handleSubmit(billingAddressOnSubmit)}
                    display="flex"
                    flexDirection="column"
                    alignItems="end"
                  >
                    <BillingForm control={form.control} setValue={form.setValue} clearErrors={form.clearErrors} />
                    <Box display="flex" justifyContent="end" gap={1} mt={2}>
                      <Button variant="outlined" size="small" onClick={onEditingBillingAddressCancel}>
                        {t('common.cancel')}
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        size="small"
                        loading={updatePending}
                        onClick={form.handleSubmit(billingAddressOnSubmit)}
                      >
                        {t('common.save')}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <BillingAddressDisplay
                    billingAddress={currentActiveOrganizationBillingAddress}
                    canEdit={canEditBillingAddress}
                    onEditClick={() => setIsEditingBillingAddress(true)}
                  />
                )}
              </Box>
              <Box
                sx={{
                  width: '100%',
                  mt: { xs: 0, sm: 'auto' },
                  pb: 2,
                  flexShrink: 0,
                }}
              >
                <PricingDisplay
                  latestOrder={latestOrder}
                  latestOrderBillingCountry={latestOrderBillingCountry}
                  vatCountryDisplay={vatCountryDisplay}
                />
              </Box>
            </Box>
          </Stack>
        </Box>
      </Modal>
      {!onOpenCatalogPicker && (
        <EventCatalogProductsListModal
          event={eventForCheckout}
          open={embeddedCatalogOpen}
          onModalClose={handleEmbeddedCatalogClose}
          onCheckoutRequested={handleEmbeddedCatalogCheckoutRequested}
        />
      )}
    </>
  );
};

export default EventCatalogOrderCheckoutModal;
